import { SEOHead } from '../components/SEOHead';
import { COMPANY_CONFIG } from '../config/site';

export function PrivacyPage() {
  const lastUpdated = 'October 21, 2026';

  return (
    <>
      <SEOHead
        title="Privacy Policy | Safaza Technologies, Inc."
        description="Privacy policy and data handling disclosures for visitors and business partners of Safaza Technologies, Inc."
      />

      <section className="pt-16 pb-12 sm:pt-24 sm:pb-16 bg-white border-b border-[#E4E7EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0D66E4]/8 border border-[#0D66E4]/20 text-[11px] font-mono font-medium text-[#0D66E4] mb-4">
            <span>LEGAL DOCUMENTATION</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#101828] mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm text-[#667085]">
            Last Updated: {lastUpdated} &middot; Effective Date: October 2026
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 text-[#475467] leading-relaxed text-sm sm:text-base">
          
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              1. Introduction &amp; Entity Identification
            </h2>
            <p className="mb-4">
              {COMPANY_CONFIG.legalName} (&quot;Safaza Technologies&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is a software
              development company incorporated in the United States. This Privacy Policy describes how we collect,
              use, and protect information when you visit our primary corporate website (
              <span className="font-mono text-xs">{COMPANY_CONFIG.primaryDomain}</span>) or communicate
              with our team regarding software engineering services, SaaS platforms, or business partnerships.
            </p>
            <p>
              This policy governs our corporate website, client inquiry workflows, and professional communication channels.
              Safaza Technologies, Inc. is a software technology company; we do not operate consumer banking, payment transmission,
              or custodial financial applications.
            </p>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              2. Information We Collect
            </h2>
            <p className="mb-4">
              We collect information strictly necessary to operate our website and communicate with
              prospective commercial partners:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Direct Inquiries:</strong> When you submit a business inquiry through our
                contact form or send an email, we collect your full name, business email address,
                company or organization name, inquiry category, and the contents of your message.
              </li>
              <li>
                <strong>Technical Server Logs:</strong> When you load pages, our hosting provider
                (Cloudflare) automatically records routine connection metadata, including client IP
                address, browser user-agent, operating system, requested URL, timestamp, and HTTP
                response codes. These logs are generated for network security, DDoS protection, and
                infrastructure reliability.
              </li>
              <li>
                <strong>Cookies &amp; Local Storage:</strong> We do not deploy third-party advertising
                or tracking cookies. Necessary technical session cookies may be used solely to manage
                routing and security.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              3. How We Use Collected Information
            </h2>
            <p className="mb-4">
              Information collected through this website is used solely for legitimate business purposes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>To evaluate and respond to commercial, product, or partnership inquiries;</li>
              <li>To protect our website infrastructure against automated attacks, spam, and abuse;</li>
              <li>To maintain compliance with applicable legal and record-keeping requirements;</li>
              <li>To provide corporate verification materials to banking and commercial partners upon authorized request.</li>
            </ul>
            <p className="mt-4 font-semibold text-[#101828]">
              We do not sell, rent, or trade your personal or business contact information to third parties.
            </p>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              4. Data Retention &amp; Security
            </h2>
            <p className="mb-4">
              We retain business inquiries only as long as necessary to fulfill the purpose for which
              they were submitted or as required by law. Routine server logs are retained in accordance
              with Cloudflare standard security retention schedules.
            </p>
            <p>
              We implement industry-standard encryption in transit (HTTPS/TLS) across our web assets
              and enforce access controls so that inquiry messages are restricted to authorized company
              representatives.
            </p>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              5. Third-Party Service Providers
            </h2>
            <p className="mb-4">
              We rely on trusted enterprise infrastructure providers to host and secure our digital
              assets:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Cloudflare, Inc.:</strong> Global Content Delivery Network (CDN), edge routing,
                DDoS mitigation, and serverless compute infrastructure.
              </li>
              <li>
                <strong>Corporate Email Provider:</strong> Secure cloud email infrastructure for
                managing incoming and outgoing business communications.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              6. Your Privacy Rights
            </h2>
            <p className="mb-4">
              Depending on your location and applicable data privacy legislation (such as GDPR or US
              state laws), you may have the right to request access to, correction of, or deletion of
              personal information you have directly submitted to us.
            </p>
            <p>
              To exercise these rights, please email us directly at{' '}
              <a href={`mailto:${COMPANY_CONFIG.contactEmail}`} className="text-[#0D66E4] underline font-medium">
                {COMPANY_CONFIG.contactEmail}
              </a>
              .
            </p>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              7. Contact Information
            </h2>
            <p>
              For questions concerning this Privacy Policy or our data handling practices, please
              contact:
            </p>
            <div className="mt-4 p-5 rounded-xl bg-[#F6F8FB] border border-[#E4E7EC] text-xs sm:text-sm font-mono space-y-1">
              <p className="font-bold text-[#101828]">{COMPANY_CONFIG.legalName}</p>
              <p>Attn: Corporate Privacy &amp; Data Inquiries</p>
              <p>Jurisdiction: {COMPANY_CONFIG.incorporationCountry}</p>
              <p>
                Email:{' '}
                <a href={`mailto:${COMPANY_CONFIG.contactEmail}`} className="text-[#0D66E4] hover:underline">
                  {COMPANY_CONFIG.contactEmail}
                </a>
              </p>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}
